import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { driverSurchargeSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { DriverSurcharge } from './model'
import { Driver } from '../drivers/model'
import * as driversActionCreators from '../drivers/actionCreators'
import { DriverSurchargeType } from '../driver_surcharge_types/model'
import * as driverSurchargeTypesActionCreators from '../driver_surcharge_types/actionCreators'
import { Currency } from '../currencies/model'
import * as currenciesActionCreators from '../currencies/actionCreators'

// Fetch

const fetchDriverSurchargesEpic = (action$, store) => {
    const tableIdentifier = 'driver_surcharges_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `driver-surcharges/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(driverSurchargeSchema)
                        )

                        const driverSurcharges = normalizedEntitiesToRecordMap(
                            normalizedData.entities.driver_surcharges,
                            DriverSurcharge,
                            normalizedData.result
                        )
                        const drivers = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver)
                        const driverSurchargeTypes = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharge_types, DriverSurchargeType)
                        const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)

                        const observables = [
                            Observable.of(driversActionCreators.fetchDriversFulfilled(drivers)),
                            Observable.of(driverSurchargeTypesActionCreators.fetchDriverSurchargeTypesFulfilled(driverSurchargeTypes)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies)),
                            Observable.of(actionCreators.fetchDriverSurchargesFulfilled(driverSurcharges)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriverSurchargesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchDriverSurchargeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().driverSurcharges.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `driver-surcharges/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driverSurchargeSchema)

                        const driverSurcharges = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharges, DriverSurcharge)
                    const driverSurchargeTypes = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharge_types, DriverSurchargeType)
                    const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchDriverSurchargesFulfilled(driverSurcharges)),
                            Observable.of(driverSurchargeTypesActionCreators.fetchDriverSurchargeTypesFulfilled(driverSurchargeTypes)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies)),
                            Observable.of(actionCreators.fetchDriverSurchargeFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriverSurchargesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveDriverSurchargeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().driverSurcharges.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `driver-surcharges`
            let method = 'POST'

            if (values.id) {
                path = `driver-surcharges/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveDriverSurchargeDuplicityFound(ajaxResponse.response.duplicity)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, driverSurchargeSchema)

                        const driverSurcharges = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharges, DriverSurcharge)
                        const driverSurcharge = driverSurcharges.valueSeq().first()

                        return Observable.of(actionCreators.saveDriverSurchargeFulfilled(driverSurcharge))
                    })
                    .catch(error => Observable.of(actionCreators.saveDriverSurchargeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteDriverSurchargeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().driverSurcharges.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `driver-surcharges/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteDriverSurchargeFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteDriverSurchargeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportDriverSurchargesEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `driver-surcharges/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [fetchDriverSurchargesEpic, fetchDriverSurchargeEpic, saveDriverSurchargeEpic, deleteDriverSurchargeEpic, exportDriverSurchargesEpic]
